/**
 * MonetizeView — top-level orchestrator of the Traction Build experience.
 *
 *   IDLE state    →  TractionBubble (centered prompt + linked-build dropdown
 *                    + 3 example traction cards + recent ventures strip).
 *
 *   ACTIVE state  →  TractionCockpit (split-screen: TractionPreview on the LEFT
 *                    with 5 living zones brand-skinned from the linked build,
 *                    CockpitChat on the RIGHT, and the "Show the docs" drawer
 *                    on demand → DocsLibraryDrawer wraps every legacy renderer).
 *
 * All existing renderers (DataroomRender, PersonasRender, BMRender,
 * InvestorsRender, GrantsRender, NetworkRender) are preserved here. They're
 * registered globally on `window.__monetizeRenderers` so the DocsLibraryDrawer
 * can pick them up without a circular import.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  ArrowLeft, ArrowRight, X, Search, Sparkles, FileText, Users, Layers,
  Globe2, Briefcase, Award, Megaphone, Loader2, Target, Wallet, DollarSign,
  Trophy, AlertTriangle, ExternalLink, Plug, Trash2, Plus, Lock,
  CheckCircle2, RefreshCw, TrendingUp,
} from 'lucide-react';

import TractionBubble from './monetize/TractionBubble';
import TractionPreview from './monetize/TractionPreview';
import CockpitChat from './monetize/CockpitChat';
import DocsLibraryDrawer from './monetize/DocsLibraryDrawer';
import { paletteForVenture, DEFAULT_PALETTE } from './monetize/brandInheritance';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ACCENT = '#FFD700';

/* =================== MAIN VIEW ROUTER =================== */
export default function MonetizeView() {
  const [items, setItems] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [bootstrapPrompt, setBootstrapPrompt] = useState(null);  // initial brief passed from bubble
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/monetize/projects`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
      }
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // Register renderers globally so DocsLibraryDrawer can pick them up
  useEffect(() => {
    window.__monetizeRenderers = {
      readme: ({ payload, color }) => <ReadmeRender d={payload} color={color} />,
      dataroom: ({ payload, cached, color }) => <DataroomRender d={payload} color={color} cached={cached} />,
      personas: ({ payload, color }) => <PersonasRender d={payload} color={color} />,
      business_models: ({ payload, color }) => <BMRender d={payload} color={color} />,
      investors: ({ payload, color }) => <InvestorsRender d={payload} color={color} />,
      grants: ({ payload, color }) => <GrantsRender d={payload} color={color} />,
      network: ({ payload, color }) => <NetworkRender d={payload} color={color} />,
    };
    return () => { delete window.__monetizeRenderers; };
  }, []);

  if (selectedId) {
    const venture = (items || []).find(v => v.monetize_id === selectedId);
    return (
      <TractionCockpit
        monetizeId={selectedId}
        seedVenture={venture}
        initialPrompt={bootstrapPrompt}
        onBack={() => { setBootstrapPrompt(null); setSelectedId(null); load(); }}
        onRequestDelete={(id) => setDeleting(id)}
      />
    );
  }

  return (
    <div
      data-testid="monetize-view"
      data-studio-theme="light"
      style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <TractionBubble
        onLaunch={(mp, ctx) => {
          setBootstrapPrompt(ctx?.prompt || null);
          setSelectedId(mp.monetize_id);
          // optimistic add so RecentVenturesStrip below shows it on return
          setItems(prev => {
            const list = prev || [];
            if (list.some(x => x.monetize_id === mp.monetize_id)) return list;
            return [mp, ...list];
          });
        }}
      />

      {/* Recent ventures strip (compact) */}
      {items && items.length > 0 && (
        <RecentVenturesStrip
          items={items}
          onOpen={(id) => { setBootstrapPrompt(null); setSelectedId(id); }}
          onDelete={(id) => setDeleting(id)}
        />
      )}

      {deleting && (
        <DeleteVentureModal
          monetizeId={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            if (selectedId === deleting) setSelectedId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/* =================== Recent ventures strip =================== */
function RecentVenturesStrip({ items, onOpen, onDelete }) {
  return (
    <div
      data-testid="recent-ventures-strip"
      style={{
        background: '#FBFAF7', borderTop: '1px solid rgba(15,15,30,0.06)',
        padding: '18px 28px 24px',
      }}
    >
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'rgba(15,15,30,0.4)', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Wallet size={11} /> Recent ventures · {items.length}
        </div>
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        }}>
          {items.slice(0, 8).map(it => (
            <VentureChip key={it.monetize_id} item={it} onOpen={() => onOpen(it.monetize_id)} onDelete={() => onDelete(it.monetize_id)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function VentureChip({ item, onOpen, onDelete }) {
  const palette = paletteForVenture(item);
  const hasFirstDollar = !!item.first_dollar_at;
  const completedCount = Object.values(item.modules || {}).filter(m => m.status === 'complete').length;
  const totalModules = Object.keys(item.modules || {}).length || 7;
  return (
    <div
      data-testid={`venture-chip-${item.monetize_id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: 10, background: '#FFFFFF',
        border: '1px solid rgba(15,15,30,0.06)',
        borderRadius: 12, cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onClick={onOpen}
      onMouseEnter={e => { e.currentTarget.style.borderColor = palette.accent + '99'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 8px 24px -10px ${palette.accent}55`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,15,30,0.06)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{
        width: 34, height: 34, flexShrink: 0,
        background: hasFirstDollar ? 'rgba(52,211,153,0.16)' : palette.accent + '22',
        border: `1px solid ${hasFirstDollar ? 'rgba(52,211,153,0.45)' : palette.accent + '55'}`,
        color: hasFirstDollar ? '#22C55E' : palette.accent,
        borderRadius: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {hasFirstDollar ? <Trophy size={15} /> : <Target size={15} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0F0F1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.name}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.5)', marginTop: 2 }}>
          {completedCount}/{totalModules} docs · score {item.score || 0}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        data-testid={`venture-chip-delete-${item.monetize_id}`}
        title="Delete"
        style={{
          width: 26, height: 26,
          background: 'transparent', border: '1px solid rgba(15,15,30,0.08)',
          color: 'rgba(15,15,30,0.4)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 6,
        }}
        onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.color = '#FF6B6B'; e.currentTarget.style.borderColor = 'rgba(255,107,107,0.5)'; }}
        onMouseLeave={e => { e.stopPropagation(); e.currentTarget.style.color = 'rgba(15,15,30,0.4)'; e.currentTarget.style.borderColor = 'rgba(15,15,30,0.08)'; }}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

/* =================== TRACTION COCKPIT (active state) =================== */
function TractionCockpit({ monetizeId, seedVenture, initialPrompt, onBack, onRequestDelete }) {
  const [data, setData] = useState(null);
  const [busyModule, setBusyModule] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [materializedZones, setMaterializedZones] = useState([]);
  const [isMaterializing, setIsMaterializing] = useState(false);
  const initialMaterializationStarted = useRef(false);

  const palette = useMemo(() => {
    return paletteForVenture(data || seedVenture || { name: 'venture' });
  }, [data, seedVenture]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch {}
  }, [monetizeId]);

  useEffect(() => { load(); }, [load]);

  // North Stars (Pillar Cards) — fetched once monetizeId is known.
  // Auto-init on backend if not present. Strategic targets, not live values.
  const [northStars, setNorthStars] = useState(null);
  useEffect(() => {
    if (!monetizeId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/monetize/${monetizeId}/north_stars`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setNorthStars(data?.pillars || []);
      } catch (_) { /* silently ignore — UI shows defaults */ }
    })();
    return () => { cancelled = true; };
  }, [monetizeId]);

  const updatePillar = useCallback(async (pillar, patch) => {
    try {
      const r = await fetch(`${API}/api/monetize/${monetizeId}/north_stars/${pillar}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const updated = await r.json();
        setNorthStars(prev => (prev || []).map(p => p.pillar === pillar ? updated : p));
      }
    } catch (_) { /* ignore */ }
  }, [monetizeId]);

  // ──────────────────────────────────────────────────────────────────
  // STRICT SEQUENCE — Aria's single linear flow (Slice 1C-A):
  //   0. Read the linked build (preview + repo + chat) and write the
  //      canonical Build README — the SOLE authoritative description of
  //      what the founder actually shipped (kills the "Emergent" leak).
  //   1. Draft the docs (dataroom) — generated FROM the README, never
  //      from the stale strategic deck cached at import time.
  //   2. Compose the unified Revenue Plan
  //      (4 sections: ICP · Positioning · Pricing · Distribution).
  // ──────────────────────────────────────────────────────────────────
  const [ariaLog, setAriaLog] = useState([]);   // local narration entries, picked up by CockpitChat
  const pushAriaLog = useCallback((kind, text) => {
    setAriaLog(prev => [...prev, {
      message_id: `aria_local_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      from_name: 'Aria',
      from_color: '#5EEAD4',
      from_kind: 'agent',
      message: text,
      kind,
      created_at: new Date().toISOString(),
    }]);
  }, []);

  useEffect(() => {
    if (!data || initialMaterializationStarted.current) return;
    initialMaterializationStarted.current = true;

    // Already fully generated? No-op — TractionPreview reads outputs directly.
    if (data?.outputs?.readme && data?.outputs?.dataroom && data?.outputs?.revenue_plan) {
      return;
    }

    setIsMaterializing(true);
    pushAriaLog('thinking', 'Reading the linked build — preview, repo files, locale strings…');

    (async () => {
      // ───────────────── Step 0 · Build README ─────────────────
      let readmeData = data?.outputs?.readme?.data || null;
      if (!readmeData) {
        pushAriaLog('progress', 'Writing the canonical README — what you actually shipped…');
        try {
          const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/readme`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ regenerate: false }),
          });
          if (r.ok) {
            const fresh = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
            if (fresh.ok) {
              const freshData = await fresh.json();
              setData(freshData);
              readmeData = freshData?.outputs?.readme?.data || null;
              if (readmeData) {
                pushAriaLog('done',
                  `README written · "${readmeData.project_name}"${readmeData.category ? ' · ' + readmeData.category : ''}.`);
              }
            }
          } else {
            pushAriaLog('warn', 'README skipped — link a build with a real preview & repo first.');
          }
        } catch {
          pushAriaLog('warn', 'README generation failed — retry from "Re-sync from build".');
        }
      }

      // ───────────────── Step 1 · Dataroom (Show the docs) ─────────────────
      let datroomData = data?.outputs?.dataroom?.data || null;
      if (!datroomData) {
        pushAriaLog('progress', 'Drafting the docs — built from the README…');
        try {
          const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/dataroom`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ regenerate: false }),
          });
          if (r.ok) {
            const fresh = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
            if (fresh.ok) {
              const freshData = await fresh.json();
              setData(freshData);
              datroomData = freshData?.outputs?.dataroom?.data || null;
              if (datroomData) {
                const competitors = (datroomData.competitor_mapping || []).length;
                pushAriaLog('done',
                  `Docs ready · ${competitors} competitors mapped${datroomData.tagline ? ' · ' + datroomData.tagline.slice(0, 60) : ''}.`);
                pushAriaLog('agent-spawn', 'Mentor agent spawned → available in the Agents space.');
              }
            }
          } else {
            pushAriaLog('warn', 'Docs generation skipped — retry from "Re-sync from build".');
          }
        } catch {
          pushAriaLog('warn', 'Docs generation failed — retry from "Re-sync from build".');
        }
      }

      // ───────────── Step 2 · Revenue Plan ─────────────
      // The backend auto-fires _safe_auto_revenue_plan after a fresh dataroom,
      // but for ventures whose dataroom predates that hook we trigger the
      // endpoint explicitly. Then we poll until the plan lands.
      if (datroomData) {
        pushAriaLog('progress', 'Composing the revenue plan — ICP · positioning · pricing · distribution…');

        // Kick off the generation (idempotent — returns cached if present).
        try {
          fetch(`${API}/api/monetize/projects/${monetizeId}/revenue-plan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ regenerate: false }),
          }).catch(() => {});
        } catch { /* ignore */ }

        // Poll the venture doc until outputs.revenue_plan is persisted.
        const start = Date.now();
        let planFound = false;
        while (Date.now() - start < 90_000) {
          await new Promise(r => setTimeout(r, 3500));
          try {
            const fresh = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
            if (fresh.ok) {
              const freshData = await fresh.json();
              if (freshData?.outputs?.revenue_plan?.data) {
                setData(freshData);
                const plan = freshData.outputs.revenue_plan.data;
                const segCount = (plan?.icp?.segments || []).length;
                const chCount = (plan?.distribution?.channels || []).length;
                pushAriaLog('done',
                  `Revenue plan composed · ${segCount} ICP segments ranked · ${chCount} acquisition channels.`);
                planFound = true;
                break;
              }
            }
          } catch { /* keep polling */ }
        }
        if (!planFound) {
          pushAriaLog('warn', 'Revenue plan still composing — refresh the venture in a moment.');
        }

        // ───────────── Step 3 · Execution Backlog ─────────────
        // The backend auto-seeds the backlog right after the revenue plan
        // lands, but we issue an idempotent POST to ensure it (and to learn
        // the count to narrate). If tasks already exist, the endpoint
        // returns the cached set without re-generating.
        if (planFound) {
          pushAriaLog('progress', 'Seeding the execution backlog — Customer · Outreach · Growth · Sales…');
          try {
            const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/execution-backlog`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ regenerate: false }),
            });
            if (r.ok) {
              const bundle = await r.json();
              const count = bundle?.count || (bundle?.tasks || []).length || 0;
              const buckets = (bundle?.tasks || []).reduce((acc, t) => {
                acc[t.execution_kind] = (acc[t.execution_kind] || 0) + 1;
                return acc;
              }, {});
              pushAriaLog('done',
                `Execution backlog ready · ${count} tasks queued (${buckets.customer || 0}C · ${buckets.outreach || 0}O · ${buckets.growth || 0}G · ${buckets.sales || 0}S). Open HQ to start shipping.`);
            } else {
              pushAriaLog('warn', 'Execution backlog will be seeded shortly in HQ.');
            }
          } catch {
            pushAriaLog('warn', 'Execution backlog will be seeded shortly in HQ.');
          }
        }
      }

      setIsMaterializing(false);
    })();
  }, [data?.monetize_id]);

  const addZone = useCallback((key) => {
    setMaterializedZones(prev => prev.includes(key) ? prev : [...prev, key]);
  }, []);

  // Force-regenerate the docs + revenue plan using the freshest project
  // context. Same linear flow as the mount effect.
  const [resyncBusy, setResyncBusy] = useState(false);
  const resyncFromBuild = async () => {
    setResyncBusy(true);
    setIsMaterializing(true);
    pushAriaLog('thinking', 'Re-reading the linked build from scratch — clearing stale assumptions…');

    // Step 0 — regenerate README first (sets the new ground truth)
    pushAriaLog('progress', 'Rewriting the canonical README…');
    try {
      const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/readme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ regenerate: true }),
      });
      if (r.ok) {
        const fresh = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
        if (fresh.ok) {
          const freshData = await fresh.json();
          setData(freshData);
          const rd = freshData?.outputs?.readme?.data;
          if (rd) {
            pushAriaLog('done',
              `README rewritten · "${rd.project_name}"${rd.category ? ' · ' + rd.category : ''}.`);
          }
        }
      }
    } catch { /* ignore */ }

    // Step 1 — regenerate dataroom (now built FROM the fresh README)
    pushAriaLog('progress', 'Redrafting the docs from the README…');
    let datroomData = null;
    try {
      const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/dataroom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ regenerate: true }),
      });
      if (r.ok) {
        const fresh = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
        if (fresh.ok) {
          const freshData = await fresh.json();
          setData(freshData);
          datroomData = freshData?.outputs?.dataroom?.data || null;
          if (datroomData) {
            pushAriaLog('done',
              `Docs regenerated · ${(datroomData.competitor_mapping || []).length} competitors mapped.`);
          }
        }
      }
    } catch { /* ignore */ }

    // Step 2 — regenerate revenue plan (force)
    if (datroomData) {
      pushAriaLog('progress', 'Recomposing the revenue plan…');
      try {
        const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/revenue-plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ regenerate: true }),
        });
        if (r.ok) {
          const fresh = await fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' });
          if (fresh.ok) {
            const freshData = await fresh.json();
            setData(freshData);
            const plan = freshData?.outputs?.revenue_plan?.data;
            if (plan) {
              const segCount = (plan?.icp?.segments || []).length;
              const chCount = (plan?.distribution?.channels || []).length;
              pushAriaLog('done',
                `Revenue plan recomposed · ${segCount} ICP segments · ${chCount} channels.`);
            }
          }
        }
      } catch { /* ignore */ }

      // Step 3 — reseed execution backlog (force)
      pushAriaLog('progress', 'Reseeding the execution backlog…');
      try {
        const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/execution-backlog`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ regenerate: true }),
        });
        if (r.ok) {
          const bundle = await r.json();
          const count = bundle?.count || (bundle?.tasks || []).length || 0;
          pushAriaLog('done', `Execution backlog reseeded · ${count} tasks queued.`);
        }
      } catch { /* ignore */ }
    }

    setIsMaterializing(false);
    setResyncBusy(false);
  };

  const generateModule = async (moduleKey, regenerate = false) => {
    setBusyModule(moduleKey);
    const endpointMap = {
      dataroom: 'dataroom',
      personas: 'personas',
      business_models: 'business-models',
      investors: 'investors',
      grants: 'grants',
      network: 'network/analyze',
    };
    const ep = endpointMap[moduleKey];
    if (!ep) { setBusyModule(null); return; }
    try {
      const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/${ep}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ regenerate }),
      });
      if (r.ok) await load();
      else {
        const err = await r.json().catch(() => ({}));
        alert(err.detail || `${moduleKey} failed`);
      }
    } catch (e) {
      alert(e.message);
    } finally { setBusyModule(null); }
  };

  const handleAgentAction = (action) => {
    // For V1, action triggers an HQ user-note that the existing Aria/Hermes
    // pipeline picks up. Surface a quick toast via the chat (CockpitChat handles
    // its own optimistic UI — we just relay through the existing endpoint).
    if (!monetizeId) return;
    const text = labelForAction(action);
    fetch(`${API}/api/hq/${monetizeId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ message: text, to_agent_id: 'aria' }),
    }).catch(() => {});
  };

  if (!data) {
    return (
      <div data-testid="cockpit-loading" style={{
        flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: palette.bg, color: palette.textMute, fontSize: 12,
      }}>
        <Loader2 size={14} style={{ animation: 'spin 0.7s linear infinite', marginRight: 8 }} />
        Loading your traction cockpit…
      </div>
    );
  }

  return (
    <div
      data-testid="traction-cockpit"
      data-palette={palette.key}
      style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Top bar */}
      <div style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 22px',
        background: palette.bg, color: palette.text,
        borderBottom: `1px solid ${palette.border}`,
      }}>
        <button
          onClick={onBack}
          data-testid="cockpit-back-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', color: palette.textMute,
            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0,
          }}
        >
          <ArrowLeft size={13} /> All ventures
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={resyncFromBuild}
            disabled={resyncBusy}
            data-testid="cockpit-resync-btn"
            title="Re-read the linked build and regenerate every doc"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 11px',
              background: resyncBusy ? `${palette.accent}1A` : 'transparent',
              color: palette.accent,
              border: `1px solid ${palette.accent}55`,
              cursor: resyncBusy ? 'wait' : 'pointer', fontFamily: 'inherit',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 6,
              transition: 'all 0.15s',
            }}
          >
            {resyncBusy
              ? <><Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite' }} /> Resyncing…</>
              : <><RefreshCw size={11} /> Re-sync from build</>}
          </button>
          <button
            onClick={() => onRequestDelete?.(monetizeId)}
            data-testid="cockpit-delete-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 11px',
              background: 'transparent', color: palette.textMute,
              border: `1px solid ${palette.border}`,
              cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 6,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#FF6B6B'; e.currentTarget.style.borderColor = 'rgba(255,107,107,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = palette.textMute; e.currentTarget.style.borderColor = palette.border; }}
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>

      {/* Split */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <TractionPreview
          venture={data}
          outputs={data.outputs || {}}
          palette={palette}
          isMaterializing={isMaterializing}
          materializedZones={materializedZones}
          northStars={northStars}
          onUpdatePillar={updatePillar}
          onTriggerAgentAction={handleAgentAction}
          onOpenDocs={() => setDocsOpen(true)}
        />
        <CockpitChat
          venture={data}
          palette={palette}
          initialPrompt={initialPrompt}
          ariaLog={ariaLog}
          onMaterializeZone={addZone}
        />
      </div>

      {/* Docs library drawer */}
      {docsOpen && (
        <DocsLibraryDrawer
          venture={data}
          outputs={data.outputs || {}}
          modules={data.modules || {}}
          palette={palette}
          busyModule={busyModule}
          onGenerate={generateModule}
          onClose={() => setDocsOpen(false)}
        />
      )}
    </div>
  );
}

function labelForAction(a) {
  switch (a?.kind) {
    case 'competitor-watch':   return `Watch competitor: ${a.target}${a.url ? ' (' + a.url + ')' : ''}`;
    case 'brief-partnership':  return `Brief partnership: ${a.target} (${a.platform || 'unknown platform'}) ${a.handle || ''}`;
    case 'outreach-list':      return `Build outreach list for ICP: ${a.target}`;
    case 'activate-path':      return `Activate traction path: ${a.target}`;
    default:                   return `Action: ${a?.kind} → ${a?.target || ''}`;
  }
}

/* =================================================================
   PRESERVED RENDERERS (used by DocsLibraryDrawer via window registry)
   ================================================================= */

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', color: 'rgba(15,15,30,0.48)', textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function Card({ children, color }) {
  return (
    <div style={{
      padding: 14, marginBottom: 8,
      background: 'rgba(15,15,30,0.025)',
      border: `1px solid ${color ? color + '20' : 'rgba(15,15,30,0.045)'}`,
    }}>{children}</div>
  );
}

function P({ children }) {
  return <div style={{ fontSize: 13, color: 'rgba(15,15,30,0.78)', lineHeight: 1.65 }}>{children}</div>;
}

function Bullets({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {items.map((it, i) => (
        <li key={i} style={{ fontSize: 12, color: 'rgba(15,15,30,0.72)', lineHeight: 1.7, marginBottom: 4 }}>{it}</li>
      ))}
    </ul>
  );
}

function ScoreBadge({ label, v, c }) {
  if (typeof v !== 'number') return null;
  return (
    <span style={{
      fontSize: 9, padding: '3px 7px', fontWeight: 700,
      background: `${c}1f`, color: c, border: `1px solid ${c}40`,
      letterSpacing: '0.05em', textTransform: 'uppercase',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {label} {v}
    </span>
  );
}

function ReadmeRender({ d, color }) {
  if (!d) {
    return (
      <div style={{ padding: 18, color: 'rgba(15,15,30,0.5)', fontSize: 12 }}>
        No README yet — generate it from the card.
      </div>
    );
  }
  const accent = color || '#5EEAD4';
  return (
    <div data-testid="readme-render" style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Hero */}
      <header style={{
        padding: '20px 22px',
        background: `linear-gradient(135deg, ${accent}1A 0%, transparent 60%), #0F172A`,
        borderRadius: 14,
        color: '#FFFFFF',
        boxShadow: `0 24px 60px -22px ${accent}55`,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.26em', textTransform: 'uppercase',
          color: accent, marginBottom: 6,
        }}>
          Canonical build README
        </div>
        <div style={{
          fontSize: 26, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.15,
          marginBottom: 4,
        }}>
          {d.project_name || 'Untitled product'}
        </div>
        {d.tagline && (
          <div style={{
            fontSize: 14, color: 'rgba(255,255,255,0.78)', fontStyle: 'italic',
            lineHeight: 1.45, maxWidth: 720,
          }}>
            “{d.tagline}”
          </div>
        )}
        {d.category && (
          <div style={{
            marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '5px 12px',
            background: `${accent}22`,
            border: `1px solid ${accent}55`,
            borderRadius: 999,
            fontSize: 11, fontWeight: 700, color: accent,
            letterSpacing: '0.06em',
          }}>
            {d.category}
          </div>
        )}
      </header>

      {/* Description */}
      {d.description && (
        <ReadmeSection title="What it does">
          <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: '#0F0F1A' }}>
            {d.description}
          </p>
        </ReadmeSection>
      )}

      {/* Two-column grid for the lists */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {Array.isArray(d.features) && d.features.length > 0 && (
          <ReadmeSection title="Features shipped" color={accent}>
            <ReadmeBullets items={d.features} />
          </ReadmeSection>
        )}
        {Array.isArray(d.key_screens) && d.key_screens.length > 0 && (
          <ReadmeSection title="Key screens" color={accent}>
            <ReadmeBullets items={d.key_screens} />
          </ReadmeSection>
        )}
        {Array.isArray(d.tech_stack) && d.tech_stack.length > 0 && (
          <ReadmeSection title="Tech stack" color={accent}>
            <ReadmeChips items={d.tech_stack} color={accent} />
          </ReadmeSection>
        )}
        {Array.isArray(d.audience_signals) && d.audience_signals.length > 0 && (
          <ReadmeSection title="Audience signals" color={accent}>
            <ReadmeBullets items={d.audience_signals} />
          </ReadmeSection>
        )}
        {Array.isArray(d.content_signals) && d.content_signals.length > 0 && (
          <ReadmeSection title="Content signals" color={accent}>
            <ReadmeBullets items={d.content_signals} />
          </ReadmeSection>
        )}
        {(d.design_tone || d.tone_of_voice || d.pricing_observed) && (
          <ReadmeSection title="Brand & pricing" color={accent}>
            <ReadmeDefList items={[
              ['Design tone', d.design_tone],
              ['Tone of voice', d.tone_of_voice],
              ['Pricing observed', d.pricing_observed],
            ]} />
          </ReadmeSection>
        )}
      </div>
    </div>
  );
}

function ReadmeSection({ title, color, children }) {
  return (
    <section style={{
      padding: '14px 16px',
      background: '#FBFAF7',
      border: '1px solid rgba(15,15,30,0.07)',
      borderRadius: 12,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: color || 'rgba(15,15,30,0.55)', marginBottom: 8,
      }}>
        {title}
      </div>
      {children}
    </section>
  );
}

function ReadmeBullets({ items }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
      {items.filter(Boolean).map((it, i) => (
        <li key={i} style={{
          fontSize: 12.5, lineHeight: 1.55, color: '#0F0F1A',
          display: 'grid', gridTemplateColumns: '10px 1fr', gap: 8,
        }}>
          <span style={{ color: 'rgba(15,15,30,0.35)' }}>·</span>
          <span>{String(it)}</span>
        </li>
      ))}
    </ul>
  );
}

function ReadmeChips({ items, color }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {items.filter(Boolean).map((it, i) => (
        <span key={i} style={{
          fontSize: 10.5, fontWeight: 700,
          padding: '4px 9px',
          background: `${color}1A`, color: '#0F0F1A',
          border: `1px solid ${color}33`,
          borderRadius: 999,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{String(it)}</span>
      ))}
    </div>
  );
}

function ReadmeDefList({ items }) {
  const filtered = items.filter(([, v]) => v);
  if (!filtered.length) return null;
  return (
    <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {filtered.map(([k, v]) => (
        <div key={k}>
          <dt style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'rgba(15,15,30,0.45)', marginBottom: 2,
          }}>{k}</dt>
          <dd style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: '#0F0F1A' }}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function DataroomRender({ d, color, cached }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const revisions = cached?.revisions || null;
  const version = cached?.version || 1;
  const revisedBy = cached?.revised_by;
  const revisedAt = cached?.revised_at;
  const isV2 = version > 1 && !!revisions;

  const SectionWithDiff = ({ sectionKey, title, children }) => {
    const rev = isV2 && revisions[sectionKey];
    if (!rev) return <Section title={title}>{children}</Section>;
    return (
      <div style={{
        position: 'relative', marginBottom: 22, padding: '8px 12px 12px',
        background: 'linear-gradient(135deg, rgba(255,215,0,0.08), rgba(255,215,0,0.02))',
        borderLeft: '3px solid #FFD700', borderRadius: 4,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', color: 'rgba(15,15,30,0.48)', textTransform: 'uppercase' }}>{title}</span>
          <span style={{
            fontSize: 8, fontWeight: 800, letterSpacing: '0.14em',
            padding: '2px 7px', textTransform: 'uppercase',
            background: '#FFD700', color: '#0F0F1A', borderRadius: 3,
          }}>REVISED</span>
        </div>
        {children}
      </div>
    );
  };

  return (
    <div>
      {isV2 && (
        <div style={{
          marginBottom: 22, padding: '12px 14px',
          background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(255,215,0,0.02))',
          border: '1px solid rgba(255,215,0,0.35)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 30, height: 30, flexShrink: 0,
            background: '#FFD700', color: '#0F0F1A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
          }}>V{version}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0F0F1A' }}>
              Revised deck by {revisedBy || 'Aria'} · {Object.keys(revisions).length} section{Object.keys(revisions).length > 1 ? 's' : ''} updated
            </div>
            <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.55)', marginTop: 2 }}>
              {revisedAt ? new Date(revisedAt).toLocaleString() : ''} · highlighted sections below
            </div>
          </div>
          <button
            onClick={() => setShowOriginal(s => !s)}
            data-testid="deck-toggle-original"
            style={{
              padding: '6px 11px', fontSize: 10, fontWeight: 700,
              background: showOriginal ? '#0F0F1A' : 'transparent',
              color: showOriginal ? '#FFD700' : 'rgba(15,15,30,0.7)',
              border: showOriginal ? '1px solid #0F0F1A' : '1px solid rgba(15,15,30,0.18)',
              cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}
          >{showOriginal ? 'Showing original' : 'Show original'}</button>
        </div>
      )}
      {showOriginal && cached?.previous_version ? (
        <DataroomRender d={cached.previous_version} color={color} cached={null} />
      ) : (
        <>
          {d.tagline && (
            <SectionWithDiff sectionKey="tagline" title="Tagline">
              <div style={{ fontSize: 18, fontWeight: 800, color, marginBottom: 6 }}>{d.tagline}</div>
            </SectionWithDiff>
          )}
          {d.elevator_pitch && (
            <SectionWithDiff sectionKey="elevator_pitch" title="Elevator pitch">
              <P>{d.elevator_pitch}</P>
            </SectionWithDiff>
          )}
          {d.problem && (
            <SectionWithDiff sectionKey="problem" title="Problem">
              <Card><P>{d.problem.summary}</P></Card>
              <Bullets items={d.problem.pain_points} />
            </SectionWithDiff>
          )}
          {d.solution && (
            <SectionWithDiff sectionKey="solution" title="Solution">
              <Card><P>{d.solution.summary}</P></Card>
              <Bullets items={d.solution.key_features} />
            </SectionWithDiff>
          )}
          {d.market_size && (
            <SectionWithDiff sectionKey="market_size" title="Market size">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                {['tam','sam','som','growth_rate'].map(k => {
                  const v = d.market_size[k];
                  if (!v) return null;
                  const display = typeof v === 'string' || typeof v === 'number'
                    ? String(v)
                    : (v?.value || v?.amount || JSON.stringify(v).slice(0, 30));
                  return (
                    <Card key={k} color={color}>
                      <div style={{ fontSize: 9, color: 'rgba(15,15,30,0.48)', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>{k}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F1A', marginTop: 4 }}>{display}</div>
                    </Card>
                  );
                })}
              </div>
            </SectionWithDiff>
          )}
          {d.target_audience && (
            <SectionWithDiff sectionKey="target_audience" title="Target audience">
              {Object.entries(d.target_audience).map(([k, v]) => {
                const display = typeof v === 'string' || typeof v === 'number'
                  ? String(v)
                  : Array.isArray(v) ? v.join(', ')
                  : JSON.stringify(v).slice(0, 200);
                return (
                  <div key={k} style={{ fontSize: 12, marginBottom: 5 }}>
                    <span style={{ color: 'rgba(15,15,30,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, fontSize: 10, marginRight: 8 }}>{k}</span>
                    <span style={{ color: '#0F0F1A' }}>{display}</span>
                  </div>
                );
              })}
            </SectionWithDiff>
          )}
          {d.swot && (
            <SectionWithDiff sectionKey="swot" title="SWOT">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[['strengths','#22C55E'],['weaknesses','#FF6B6B'],['opportunities','#06B6D4'],['threats','#F97316']].map(([k,c]) => (
                  <Card key={k} color={c}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: c, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{k}</div>
                    <Bullets items={d.swot[k]} />
                  </Card>
                ))}
              </div>
            </SectionWithDiff>
          )}
          {Array.isArray(d.competitor_mapping) && d.competitor_mapping.length > 0 && (
            <SectionWithDiff sectionKey="competitor_mapping" title="Competitor mapping">
              {d.competitor_mapping.map((c, i) => (
                <Card key={i} color={color}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F1A' }}>{c.name}</div>
                    {c.positioning && <span style={{ fontSize: 9, padding: '2px 6px', background: `${color}25`, color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{c.positioning}</span>}
                  </div>
                  {c.strengths && <P>+ {c.strengths}</P>}
                  {c.weaknesses && <P>− {c.weaknesses}</P>}
                  {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color, fontFamily: "'JetBrains Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 4 }}><ExternalLink size={10} /> {c.url}</a>}
                </Card>
              ))}
            </SectionWithDiff>
          )}
          {d.differentiation && <SectionWithDiff sectionKey="differentiation" title="Differentiation"><Bullets items={d.differentiation} /></SectionWithDiff>}
          {d.go_to_market && (
            <SectionWithDiff sectionKey="go_to_market" title="Go-to-market">
              {d.go_to_market.channels && <Card><div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(15,15,30,0.55)', textTransform: 'uppercase', marginBottom: 6 }}>Channels</div><Bullets items={d.go_to_market.channels} /></Card>}
              {Array.isArray(d.go_to_market.phases) && d.go_to_market.phases.map((ph, i) => (
                <Card key={i}><div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A' }}>{ph.name} · {ph.duration}</div><P>{ph.goal}</P></Card>
              ))}
            </SectionWithDiff>
          )}
          {d.revenue_forecast && (
            <SectionWithDiff sectionKey="revenue_forecast" title="Revenue forecast">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                {['year_1','year_2','year_3'].map(k => {
                  const v = d.revenue_forecast[k];
                  if (!v) return null;
                  const total = typeof v === 'string' ? v : (v?.total || v?.amount || JSON.stringify(v).slice(0, 40));
                  const breakdown = typeof v === 'object' ? (v.breakdown || null) : null;
                  return (
                    <Card key={k} color={color}>
                      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.48)' }}>{k.replace('_',' ')}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color, marginTop: 4 }}>{total}</div>
                      {breakdown && typeof breakdown === 'object' && (
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(15,15,30,0.06)' }}>
                          {Object.entries(breakdown).map(([bk, bv]) => (
                            <div key={bk} style={{ fontSize: 10, color: 'rgba(15,15,30,0.6)', marginBottom: 2 }}>
                              <span style={{ fontWeight: 700, color: 'rgba(15,15,30,0.75)' }}>{bk.replace(/_/g, ' ')}: </span>{String(bv)}
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
              {Array.isArray(d.revenue_forecast.key_assumptions) && <div style={{ marginTop: 10 }}><Bullets items={d.revenue_forecast.key_assumptions} /></div>}
            </SectionWithDiff>
          )}
          {d.ask && (
            <SectionWithDiff sectionKey="ask" title="Investment ask">
              <Card color={color}>
                <div style={{ fontSize: 18, fontWeight: 800, color }}>{d.ask.amount}</div>
                {Array.isArray(d.ask.use_of_funds) && (
                  <div style={{ marginTop: 8 }}>
                    {d.ask.use_of_funds.map((u, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                        <span style={{ color: 'rgba(15,15,30,0.72)' }}>{u.bucket}</span>
                        <span style={{ fontWeight: 700, color: '#0F0F1A' }}>{u.percent}%</span>
                      </div>
                    ))}
                  </div>
                )}
                {d.ask.milestones && <div style={{ marginTop: 8 }}><Bullets items={d.ask.milestones} /></div>}
              </Card>
            </SectionWithDiff>
          )}
          {d.risks && <SectionWithDiff sectionKey="risks" title="Risks"><Bullets items={d.risks} /></SectionWithDiff>}
          {d.next_72h_actions && (
            <SectionWithDiff sectionKey="next_72h_actions" title="Next 72h">
              <Card color="#22C55E">
                <Bullets items={d.next_72h_actions} />
              </Card>
            </SectionWithDiff>
          )}
        </>
      )}
    </div>
  );
}

function PersonasRender({ d, color }) {
  return (
    <div>
      {d.ranking_rationale && (<Card color={color}><P>{d.ranking_rationale}</P></Card>)}
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', marginTop: 12 }}>
        {(d.personas || []).map(p => (
          <Card key={p.id} color={color}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0F0F1A' }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.6)', marginTop: 2 }}>{p.role} · {p.company_type}</div>
              </div>
              {typeof p.score === 'number' && (
                <div style={{ fontSize: 18, fontWeight: 800, color: p.score >= 75 ? '#22C55E' : p.score >= 50 ? color : '#FF6B6B', fontFamily: "'JetBrains Mono', monospace" }}>{p.score}</div>
              )}
            </div>
            {p.willingness_to_pay && <div style={{ fontSize: 11, color: '#22C55E', marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }}>WTP · {p.willingness_to_pay}</div>}
            {p.feedback_quote && (
              <div style={{
                fontSize: 12, fontStyle: 'italic', color: 'rgba(15,15,30,0.78)',
                padding: '10px 12px', marginTop: 10,
                background: 'rgba(15,15,30,0.03)', borderLeft: `3px solid ${color}`,
                lineHeight: 1.6,
              }}>"{p.feedback_quote}"</div>
            )}
            {p.goals && <div style={{ marginTop: 10 }}><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.48)', marginBottom: 4 }}>Goals</div><Bullets items={p.goals} /></div>}
            {p.pains && <div style={{ marginTop: 8 }}><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FF6B6B', marginBottom: 4 }}>Pains</div><Bullets items={p.pains} /></div>}
            {p.objections && <div style={{ marginTop: 8 }}><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#F97316', marginBottom: 4 }}>Objections</div><Bullets items={p.objections} /></div>}
            {p.channels && <div style={{ marginTop: 8 }}><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.48)', marginBottom: 4 }}>Channels</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{p.channels.map(c => <span key={c} style={{ fontSize: 9, padding: '2px 6px', background: `${color}1f`, color, border: `1px solid ${color}40` }}>{c}</span>)}</div></div>}
          </Card>
        ))}
      </div>
    </div>
  );
}

function BMRender({ d, color }) {
  return (
    <div>
      {d.recommended_id && (
        <Card color="#22C55E">
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#22C55E', marginBottom: 6 }}>Aria recommends</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0F0F1A' }}>
            {(d.models || []).find(m => m.id === d.recommended_id)?.name || d.recommended_id}
          </div>
          {d.rationale && <P>{d.rationale}</P>}
        </Card>
      )}
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', marginTop: 12 }}>
        {(d.models || []).map(m => (
          <Card key={m.id} color={m.id === d.recommended_id ? '#22C55E' : color}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#0F0F1A' }}>{m.name}</div>
                <div style={{ fontSize: 10, color, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>{m.type}</div>
              </div>
            </div>
            {m.tagline && <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.72)', marginTop: 6, fontStyle: 'italic' }}>{m.tagline}</div>}
            {m.description && <P>{m.description}</P>}
            {m.pricing && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 6, marginTop: 10 }}>
                {m.pricing.map((tier, i) => (
                  <div key={i} style={{ padding: 8, background: 'rgba(15,15,30,0.03)', border: '1px solid rgba(15,15,30,0.06)' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(15,15,30,0.55)' }}>{tier.tier}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#0F0F1A', marginTop: 2 }}>{tier.price}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 4, marginTop: 10, flexWrap: 'wrap' }}>
              <ScoreBadge label="Speed" v={m.speed_score} c="#22C55E" />
              <ScoreBadge label="Scale" v={m.scalability_score} c="#06B6D4" />
              <ScoreBadge label="Moat" v={m.moat_score} c="#A855F7" />
            </div>
            {m.first_dollar_path && (
              <div style={{ marginTop: 10, padding: 10, background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.25)' }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: ACCENT, marginBottom: 4 }}>First $ path</div>
                <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.78)' }}>{m.first_dollar_path}</div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function InvestorsRender({ d, color }) {
  return (
    <div>
      {d.top_3_priority && (
        <Card color="#22C55E">
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#22C55E', marginBottom: 6 }}>Top 3 priority</div>
          <Bullets items={d.top_3_priority} />
        </Card>
      )}
      {(d.investors || []).map((inv, i) => (
        <Card key={i} color={color}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0F0F1A' }}>{inv.name}</div>
              <div style={{ fontSize: 10, color, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 }}>{inv.type} · {inv.stage}</div>
            </div>
            {typeof inv.match_score === 'number' && <div style={{ fontSize: 16, fontWeight: 800, color: inv.match_score >= 75 ? '#22C55E' : color, fontFamily: "'JetBrains Mono', monospace" }}>{inv.match_score}</div>}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.7)', marginTop: 6 }}>
            {inv.ticket_min}-{inv.ticket_max} · {(inv.geography || []).join(', ')} · {(inv.sector_focus || []).join(', ')}
          </div>
          {inv.why_match && <P>{inv.why_match}</P>}
          {inv.warm_intro_path && <div style={{ fontSize: 11, color: '#22C55E', marginTop: 6 }}>→ {inv.warm_intro_path}</div>}
          {inv.outreach_subject && (
            <div style={{ marginTop: 10, padding: 10, background: 'rgba(15,15,30,0.025)', border: '1px solid rgba(15,15,30,0.06)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#0F0F1A' }}>{inv.outreach_subject}</div>
              <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.72)', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{inv.outreach_body}</div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function GrantsRender({ d, color }) {
  return (
    <div>
      {d.total_addressable_funding && (
        <Card color="#22C55E">
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#22C55E', marginBottom: 4 }}>Total addressable funding</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#22C55E', fontFamily: "'JetBrains Mono', monospace" }}>{d.total_addressable_funding}</div>
        </Card>
      )}
      {d.easy_wins && (
        <Card color={ACCENT}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: ACCENT, marginBottom: 6 }}>Easy wins</div>
          <Bullets items={d.easy_wins} />
        </Card>
      )}
      {(d.grants || []).map((g, i) => (
        <Card key={i} color={color}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0F0F1A' }}>{g.name}</div>
              <div style={{ fontSize: 10, color, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{g.type} · {g.deadline}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
              <span style={{ fontSize: 12, color: '#22C55E', fontWeight: 800 }}>{g.amount}</span>
              {typeof g.match_score === 'number' && <ScoreBadge label="Fit" v={g.match_score} c={color} />}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.65)', marginTop: 6 }}>
            {g.stage_fit} · {(g.geography || []).join(', ')} · effort: {g.effort_estimate}
          </div>
          {g.first_step && <div style={{ fontSize: 11, color: '#22C55E', marginTop: 6 }}>→ {g.first_step}</div>}
          {g.url && <a href={g.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color, marginTop: 6, fontFamily: "'JetBrains Mono', monospace" }}><ExternalLink size={10} /> {g.url}</a>}
        </Card>
      ))}
    </div>
  );
}

function NetworkRender({ d, color }) {
  return (
    <div>
      {d.summary && <Card color={color}><P>{d.summary}</P></Card>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginTop: 10 }}>
        <Card color={color}><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.55)' }}>Estimated reach</div><div style={{ fontSize: 18, fontWeight: 800, color: '#0F0F1A', marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{d.estimated_reach}</div></Card>
        <Card color="#22C55E"><div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.55)' }}>Warm leads</div><div style={{ fontSize: 18, fontWeight: 800, color: '#22C55E', marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{d.estimated_warm_leads}</div></Card>
      </div>
      {Array.isArray(d.first_messages) && (
        <Section title="First messages">
          {d.first_messages.map((m, i) => (
            <Card key={i} color={color}>
              <div style={{ fontSize: 10, color, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>{m.platform} → {m.persona}</div>
              {m.subject && <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A', marginTop: 4 }}>{m.subject}</div>}
              <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.72)', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.body}</div>
            </Card>
          ))}
        </Section>
      )}
      {Array.isArray(d.next_24h_targets) && (
        <Section title="Next 24h targets">
          <Bullets items={d.next_24h_targets.map(t => `${t.platform} — ${t.action} (${t.goal})`)} />
        </Section>
      )}
    </div>
  );
}

/* =================== Delete venture modal (preserved) =================== */
function DeleteVentureModal({ monetizeId, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/monetize/projects/${monetizeId}/deletion-impact`, { credentials: 'include' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancel) setImpact(d);
      } catch (e) {
        if (!cancel) setLoadErr(e.message);
      }
    })();
    return () => { cancel = true; };
  }, [monetizeId]);

  const confirmWord = 'DELETE';
  const ready = typed === confirmWord && !busy;

  const submit = async () => {
    if (!ready) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/api/monetize/projects/${monetizeId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      onDeleted?.();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div onClick={busy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)',
      backdropFilter: 'blur(10px)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, animation: 'fadeIn 0.18s ease-out',
    }}>
      <div onClick={e => e.stopPropagation()} data-testid="delete-venture-modal" style={{
        width: '100%', maxWidth: 580, maxHeight: '90vh',
        background: '#FFFFFF',
        border: '1px solid rgba(255,107,107,0.4)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 30px 90px rgba(255,107,107,0.18)',
        borderRadius: 14,
      }}>
        <div style={{
          padding: '20px 22px',
          borderBottom: '1px solid rgba(15,15,30,0.06)',
          display: 'flex', alignItems: 'flex-start', gap: 14,
        }}>
          <div style={{
            width: 42, height: 42, flexShrink: 0,
            background: 'rgba(255,107,107,0.12)',
            border: '1px solid rgba(255,107,107,0.45)',
            color: '#FF6B6B',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 10,
          }}>
            <AlertTriangle size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: '#FF6B6B',
            }}>Destructive action</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0F0F1A', marginTop: 4 }}>
              Delete this venture?
            </div>
            <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.6)', marginTop: 4, lineHeight: 1.55 }}>
              This will wipe the monetization layer for{' '}
              <strong style={{ color: '#0F0F1A' }}>{impact?.name || '…'}</strong>.
              The original Workspace project will not be touched.
            </div>
          </div>
          <button
            onClick={onClose} disabled={busy} data-testid="delete-modal-close"
            style={{
              width: 26, height: 26, flexShrink: 0,
              background: 'rgba(15,15,30,0.035)', border: '1px solid rgba(15,15,30,0.08)',
              color: 'rgba(15,15,30,0.6)', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 5,
            }}
          >
            <X size={13} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          {loadErr ? (
            <div style={{ fontSize: 12, color: '#FF6B6B' }}>Could not load impact: {loadErr}</div>
          ) : !impact ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(15,15,30,0.55)', fontSize: 12 }}>
              <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> Computing what will be deleted…
            </div>
          ) : (
            <ImpactList impact={impact} />
          )}
        </div>

        <div style={{
          padding: '18px 22px',
          borderTop: '1px solid rgba(15,15,30,0.06)',
          background: 'rgba(255,107,107,0.025)',
        }}>
          <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.7)', marginBottom: 10, lineHeight: 1.6 }}>
            Type{' '}
            <code style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11, fontWeight: 700,
              padding: '2px 6px',
              background: 'rgba(255,107,107,0.12)',
              color: '#FF6B6B',
              border: '1px solid rgba(255,107,107,0.3)',
            }}>{confirmWord}</code>{' '}to confirm.
          </div>
          <input
            value={typed} onChange={e => setTyped(e.target.value)}
            placeholder={confirmWord} autoFocus spellCheck={false}
            data-testid="delete-confirm-input" disabled={busy || !impact}
            style={{
              width: '100%', padding: '10px 12px', boxSizing: 'border-box',
              background: 'rgba(0,0,0,0.04)',
              border: `1px solid ${typed && typed !== confirmWord ? '#FF6B6B' : 'rgba(15,15,30,0.08)'}`,
              color: '#0F0F1A', fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13, fontWeight: 700, letterSpacing: '0.08em',
              outline: 'none',
              transition: 'border-color 0.15s',
              borderRadius: 6,
            }}
          />
          {error && <div style={{ marginTop: 8, fontSize: 11, color: '#FF6B6B' }}>Failed: {error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button onClick={onClose} disabled={busy} style={{
              flex: 1, padding: '10px 14px',
              background: 'transparent', color: 'rgba(15,15,30,0.72)',
              border: '1px solid rgba(15,15,30,0.08)',
              cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              borderRadius: 6,
            }}>Cancel</button>
            <button onClick={submit} disabled={!ready} data-testid="delete-confirm-btn" style={{
              flex: 1, padding: '10px 14px',
              background: ready ? '#FF6B6B' : 'rgba(255,107,107,0.18)',
              color: ready ? '#FFFFFF' : 'rgba(255,107,107,0.5)',
              border: 'none', cursor: ready ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
              fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'all 0.15s',
              borderRadius: 6,
            }}>
              {busy ? (
                <><Loader2 size={12} style={{ animation: 'spin 0.7s linear infinite' }} /> Deleting…</>
              ) : (
                <><Trash2 size={12} /> Delete permanently</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImpactList({ impact }) {
  const moduleCount = impact.modules?.length || 0;
  const agentCount = impact.agents?.length || 0;
  const hasFirstDollar = !!impact.first_dollar_at;
  const items = [];

  items.push({
    Icon: Wallet, color: '#FFD700',
    label: 'Venture record',
    detail: `Score ${impact.score} · ${impact.revenue_logs} revenue log${impact.revenue_logs === 1 ? '' : 's'}`,
    deleting: ['The venture itself, its score and timeline'],
  });

  if (moduleCount > 0) {
    items.push({
      Icon: FileText, color: '#06B6D4',
      label: `${moduleCount} generated doc${moduleCount === 1 ? '' : 's'}`,
      deleting: impact.modules.map(m => m.label),
    });
  }
  if (agentCount > 0) {
    const grouped = {};
    for (const a of impact.agents) {
      const k = a.kind_label || a.kind;
      if (!grouped[k]) grouped[k] = [];
      grouped[k].push(a);
    }
    items.push({
      Icon: Users, color: '#A855F7',
      label: `${agentCount} project agent${agentCount === 1 ? '' : 's'} will be removed`,
      grouped,
    });
  }
  if (hasFirstDollar) {
    items.push({
      Icon: Trophy, color: '#22C55E',
      label: 'First dollar milestone',
      detail: 'Recorded — will be lost forever',
      warn: true,
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => <ImpactRow key={i} item={it} />)}
      {moduleCount === 0 && agentCount === 0 && (
        <div style={{
          fontSize: 11, color: 'rgba(15,15,30,0.55)', lineHeight: 1.55,
          padding: 10,
          background: 'rgba(15,15,30,0.025)',
          border: '1px solid rgba(15,15,30,0.045)',
          borderRadius: 6,
        }}>Nothing has been generated yet — only the venture record will be removed.</div>
      )}
    </div>
  );
}

function ImpactRow({ item }) {
  const { Icon, color, label, detail, deleting, grouped, warn } = item;
  return (
    <div style={{
      padding: 12,
      background: warn ? 'rgba(34,197,94,0.05)' : 'rgba(15,15,30,0.025)',
      border: `1px solid ${warn ? 'rgba(34,197,94,0.25)' : 'rgba(15,15,30,0.06)'}`,
      display: 'flex', gap: 12,
      borderRadius: 8,
    }}>
      <div style={{
        width: 30, height: 30, flexShrink: 0,
        background: `${color}1f`, color,
        border: `1px solid ${color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 6,
      }}>
        <Icon size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A' }}>{label}</div>
        {detail && <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.6)', marginTop: 2 }}>{detail}</div>}
        {deleting && (
          <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {deleting.map((d, i) => (
              <span key={i} style={{
                fontSize: 10, padding: '3px 7px',
                background: 'rgba(15,15,30,0.035)',
                border: '1px solid rgba(15,15,30,0.06)',
                color: 'rgba(15,15,30,0.72)',
                borderRadius: 4,
              }}>{d}</span>
            ))}
          </div>
        )}
        {grouped && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(grouped).map(([kind, list]) => (
              <div key={kind} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: 'rgba(15,15,30,0.48)', minWidth: 88, marginTop: 4,
                }}>{kind}</span>
                <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {list.map(a => (
                    <span key={a.agent_id} style={{
                      fontSize: 10, padding: '3px 7px',
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: `${a.color}14`, color: a.color,
                      border: `1px solid ${a.color}40`,
                      borderRadius: 4,
                    }}>
                      <span style={{ fontSize: 8, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", opacity: 0.8 }}>
                        {a.avatar || a.name.slice(0, 2).toUpperCase()}
                      </span>
                      {a.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
